import * as React from 'react';
import {
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  ColumnDef,
  DisplayColumnDef,
  FilterMeta,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  RowData,
  Table,
  TableOptions,
} from '@tanstack/table-core';
import produce from 'immer';

import {fuzzyMatch} from 'common/util/fuzzy-match';
import {numericSortOrder, rowSortComparator} from 'common/report/util/sorting/table-sorting';
import {SortOrder} from 'common/types/reports/bookmark';
import {RawNumberCellType, TextCellType} from 'common/components/table-react/cell-types';
import {useLocalStorageManager} from 'common/contexts/react/storage-manager-context';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {LocalStorageKey} from 'common/report/constants';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {getProjectId} from 'common/report/util';
import {useMountEffect} from 'common/hooks/useMountEffect';
import {reporter} from 'common/report/error-reporting';

import {AbridgedCellContext, AbridgedColumnDef, BuiltInCellType, CellType, CellTypeDef, ColumnDefMeta} from './types';
import {getCellText} from './utils';

function getCellTextFromRow(row: Row<any>, columnId: string): string {
  // note: unfortunately it seems like this private method `row._getAllCellsByColumnId()` is the only practical way to get the cell efficiently
  const cell = row._getAllCellsByColumnId()[columnId];
  return getCellText(row.getValue(columnId), cell?.column?.columnDef);
}

function globalFuzzyMatchFilter<TData extends RowData>(
  row: Row<TData>,
  columnId: string,
  filterValue: any,
  addMeta: (meta: FilterMeta) => void,
): boolean {
  const cellText = getCellTextFromRow(row, columnId);
  const matchResult = fuzzyMatch(cellText, filterValue);
  if (matchResult) {
    addMeta({matchResult} as FilterMeta);
    return true;
  }
  return false;
}

function getCellSortableValueFromRow(row: Row<any>, columnId: string): string | number | boolean {
  // note: unfortunately it seems like this private method `row._getAllCellsByColumnId()` is the only practical way to get the cell efficiently
  const cell = row._getAllCellsByColumnId()[columnId];
  if (cell) {
    const columnMetadata: ColumnDefMeta | null | undefined = cell?.column?.columnDef?.meta;
    if (columnMetadata?.sortRankTransform) {
      return columnMetadata.sortRankTransform(row.getValue(columnId), cell.getContext());
    }
  }

  const value = row.getValue(columnId);
  if ([`string`, `number`, `boolean`].includes(typeof value)) {
    return value as any;
  }

  return getCellTextFromRow(row, columnId);
}

function oldTableRowSort<TData>(rowA: Row<TData>, rowB: Row<TData>, columnId: string): -1 | 0 | 1 {
  const rank = rowSortComparator(
    {v: getCellSortableValueFromRow(rowA, columnId)} as any,
    {v: getCellSortableValueFromRow(rowB, columnId)} as any,
    {sortOrder: numericSortOrder(SortOrder.Ascending), sortField: `v`},
  );
  if (rank > 0) {
    return 1;
  }
  if (rank < 0) {
    return -1;
  }
  return 0;
}

function getCellTypeDef(
  cellType: string,
  customCellTypes: Record<string, CellTypeDef<any, any>> | null | undefined,
): CellTypeDef<unknown, any> {
  const custom = customCellTypes?.[cellType];
  if (custom) {
    return custom;
  }

  // eslint-disable-next-line sonarjs/no-small-switch
  switch (cellType) {
    case `text`:
      return TextCellType;
    case `rawnumber`:
      return RawNumberCellType;
    default:
      throw new Error(`Unknown cell type: ${cellType}`);
  }
}

export interface TableReactHookOptions<
  TData extends RowData,
  CustomCellTypes extends Record<string, CellTypeDef<TData, any>> = Record<string, CellTypeDef<TData, any>>,
> {
  columns: Array<AbridgedColumnDef<TData, BuiltInCellType | CellType<TData, CustomCellTypes>>>;
  data: TData[];
  customCellTypes?: CustomCellTypes;
  defaultGlobalFilter?: string;
  defaultSorting?: SortingState;
  enableRowSelection?: boolean;
  /**
   * Controls table's internal columnFilter state. If this value is left unset, table.getState().columnFilters will be undefined,
   * and filterFn field of column will not be executed. If a column is filterable in a table, this is required.
   *
   */
  initializedColumnFilterState?: ColumnFiltersState;
  /** this is what the column filter state defaults are -- IE: if reset filters were clicked */
  defaultColumnFilterState?: ColumnFiltersState;
  disableColumnHeaders?: boolean;
}

export function useTable<TData extends RowData, CustomCellTypes extends Record<string, CellTypeDef<TData, any>>>(
  options: TableReactHookOptions<TData, CustomCellTypes>,
) {
  const [globalFilter, setGlobalFilter] = React.useState(options.defaultGlobalFilter);
  const [sorting, setSorting] = React.useState<SortingState>(options.defaultSorting ?? []);

  const columnHelper = createColumnHelper<TData>();
  const columns = options.columns
    .map((column): ColumnDef<TData> => {
      if (`accessor` in column) {
        const meta: ColumnDefMeta = {
          ...getCellTypeDef(column.cellType, options.customCellTypes),
          fixedSize: column.fixedSize,
          columnNoFilterLabel: column.columnNoFilterLabel,
          headerStyleOverrides: column.headerStyleOverrides,
          columnSelectionParams: column.columnSelectionParams,
        };
        return columnHelper.accessor(column.accessor, {
          id: column.id,
          cell: (info) => {
            const localMeta: ColumnDefMeta | undefined = info.column.columnDef.meta;
            const cellContext: AbridgedCellContext<TData, any> = {
              cellContext: info,
              getFilterMatch() {
                return this.cellContext.row.columnFiltersMeta[this.cellContext.column.id]?.matchResult;
              },
            };
            return localMeta!.template!(info.getValue(), cellContext, column.templateParams);
          },
          header: column.headerTitle,
          size: column.fractionSize,
          ...(column.minSize ? {minSize: column.minSize} : {}),
          ...(column.maxSize ? {maxSize: column.maxSize} : {}),
          enableGlobalFilter: column.enableGlobalFilter,
          enableSorting: column.enableSorting,
          sortingFn: column.sortingFn ?? (`oldTable` as any),
          filterFn: column.filterFn,
          enableColumnFilter: column.enableColumnFilter,
          meta,
        });
      }
      if (`cell` in column) {
        const meta: ColumnDefMeta = {
          fixedSize: column.fixedSize,
          headerStyleOverrides: column.headerStyleOverrides,
          columnSelectionParams: column.columnSelectionParams,
        };
        return columnHelper.display({
          id: column.id,
          cell: column.cell as DisplayColumnDef<TData>[`cell`],
          header: column.headerTitle,
          size: column.fractionSize,
          ...(column.minSize ? {minSize: column.minSize} : {}),
          ...(column.maxSize ? {maxSize: column.maxSize} : {}),
          meta,
        });
      }
      throw new Error(`Invalid table column definition`);
    })
    .filter((column) => column !== null);

  const tanstackOptions: TableOptions<TData> = {
    columns,
    columnResizeMode: `onChange`,
    data: options.data,
    getCoreRowModel: getCoreRowModel<TData>(),
    getFilteredRowModel: getFilteredRowModel<TData>(),
    getSortedRowModel: getSortedRowModel<TData>(),
    getFacetedRowModel: getFacetedRowModel<TData>(),
    getFacetedUniqueValues: getFacetedUniqueValues<TData>(),
    enableFilters: true,
    filterFns: {
      // TODO: properly declare the FilterFns typedef via declaration merging (https://tanstack.com/table/latest/docs/api/features/global-filtering#filterfns)
      fuzzy: globalFuzzyMatchFilter,
    },
    sortingFns: {
      // TODO: properly declare the FilterFns typedef via declaration merging (https://tanstack.com/table/v8/docs/api/features/sorting/#sortingfns)
      oldTable: oldTableRowSort,
    },
    state: {
      globalFilter,
      sorting,
      columnFilters: options.initializedColumnFilterState, // this will take precedence over initialState.columnFilters
    },
    initialState: {
      columnFilters: options.defaultColumnFilterState,
      columnVisibility: {
        ...options.columns.reduce(
          (acc, column) => {
            if (
              column.id &&
              (column?.isHiddenColumnForFilter ||
                (column.columnSelectionParams && !column.columnSelectionParams.isDefault))
            ) {
              acc[column.id] = false;
            }
            return acc;
          },
          {} as Record<string, boolean>,
        ),
      },
      columnSizing: options.columns.reduce(
        (acc, column) => {
          if (column.id && column.fixedSize) {
            acc[column.id] = column.fixedSize;
          }
          return acc;
        },
        {} as Record<string, number>,
      ),
      columnPinning: {
        left: options.columns.filter((column) => column.isPinnedLeft).map((column) => column.id!),
        right: options.columns.filter((column) => column.isPinnedRight).map((column) => column.id!),
      },
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    globalFilterFn: `fuzzy` as any,
    enableRowSelection: options.enableRowSelection,
    defaultColumn: {
      minSize: 135,
      maxSize: 350,
    }, // default column min/max sizes based on header cell widths
  };
  return useReactTable(tanstackOptions);
}

export const TableGroupContext = React.createContext<Nullable<Table<any>>>(null);

export function useTableGroupContext<TData extends RowData>(): Table<TData> {
  const table = React.useContext(TableGroupContext);
  if (!table) {
    throw new Error(`No table group found`);
  }
  return table;
}

type ColumnSelections = {
  key: string;
  selections: Array<{
    id: string;
    colWidth: number | null;
  }>;
};

export const useColumnSelectionStorage = <TableCellType, RowDataType>(
  key: string,
  tableColumns: Array<AbridgedColumnDef<RowDataType, TableCellType>>,
  enableColumnSelectionStorage?: boolean,
) => {
  const [modifiedTableColumns, setModifiedTableColumns] = React.useState(() => tableColumns);
  const localStorageManager = useLocalStorageManager();
  const columnSelectionStorageRef = React.useRef<UserLocalStorageHelper<ColumnSelections[]>>(
    new UserLocalStorageHelper<ColumnSelections[]>({
      namespace: LocalStorageKey.ReactTableSelection,
      context: localStorageManager,
    }),
  );

  React.useEffect(() => {
    if (enableColumnSelectionStorage) {
      // tableColumns may change (in length and content), so update accordingly but maintain columnSelectionParams order and selection if set
      setModifiedTableColumns((oldTableColumns) =>
        produce(tableColumns, (draft) => {
          draft.forEach((column) => {
            if (column.columnSelectionParams) {
              const modifiedColumnSelectionParams = oldTableColumns.find(
                (col) => col.id === column.id,
              )?.columnSelectionParams;
              if (modifiedColumnSelectionParams?.order !== undefined) {
                column.columnSelectionParams.order = modifiedColumnSelectionParams.order;
              }
              if (modifiedColumnSelectionParams?.isSelected !== undefined) {
                column.columnSelectionParams.isSelected = modifiedColumnSelectionParams.isSelected;
              }
              if (modifiedColumnSelectionParams?.size !== undefined) {
                column.columnSelectionParams.size = modifiedColumnSelectionParams.size;
              }
            }
          });
          return draft;
        }),
      );
    }
  }, [tableColumns, enableColumnSelectionStorage]);

  const userId = userCookieUtils.getUserIdentityFromCookie()?.id ?? 0;
  const projectId = getProjectId();

  const getColumnSelectionsByKey = React.useCallback(
    (columnSelectionsList: ColumnSelections[]) => {
      return columnSelectionsList?.find((col) => col.key === key);
    },
    [key],
  );

  const checkSelectionIsValid = React.useCallback(
    (selection: string): boolean => {
      return Boolean(tableColumns?.find((col) => col.id === selection)?.columnSelectionParams);
    },
    [tableColumns],
  );

  const generateSelectionsFromSaved = React.useCallback(
    (selections: string[]): ColumnSelections => {
      return {
        key,
        selections: selections.reduce(
          (acc, selection) => {
            if (checkSelectionIsValid(selection)) {
              acc.push({id: selection, colWidth: null});
            }
            return acc;
          },
          [] as ColumnSelections[`selections`],
        ),
      };
    },
    [checkSelectionIsValid, key],
  );

  const saveColumnSelections = (newSelections: string[]) => {
    const oldColumnSelectionsList = columnSelectionStorageRef.current.getItem({userId, key: projectId});
    let newColumnSelectionsList = null;
    if (!oldColumnSelectionsList) {
      // not common - if local storage is empty at time of save, reinitialize with new selections
      newColumnSelectionsList = [generateSelectionsFromSaved(newSelections)];
    } else {
      newColumnSelectionsList = produce(oldColumnSelectionsList, (draft) => {
        const oldColumnSelections = getColumnSelectionsByKey(oldColumnSelectionsList);

        if (oldColumnSelections) {
          oldColumnSelections.selections = newSelections.reduce(
            (acc, selection) => {
              const oldSelection = oldColumnSelections.selections.find((col) => col.id === selection);
              if (oldSelection) {
                acc.push(oldSelection);
              } else if (checkSelectionIsValid(selection)) {
                acc.push({id: selection, colWidth: null});
              }
              return acc;
            },
            [] as ColumnSelections[`selections`],
          );
        } else {
          // not common - if local storage is empty at time of save, reinitialize with new selections
          draft.push(generateSelectionsFromSaved(newSelections));
        }
      });
    }
    columnSelectionStorageRef.current.setItem({userId, key: projectId, value: newColumnSelectionsList});
  };

  const saveColumnSize = React.useCallback(
    (columnId: string, colWidth: number) => {
      const oldColumnSelectionsList = columnSelectionStorageRef.current.getItem({userId, key: projectId});

      let newColumnSelectionsList = null;
      if (oldColumnSelectionsList) {
        newColumnSelectionsList = produce(oldColumnSelectionsList, (draft) => {
          const oldColumnSelections = getColumnSelectionsByKey(draft);

          if (oldColumnSelections) {
            oldColumnSelections.selections = oldColumnSelections.selections.map((selection) => {
              if (selection.id === columnId) {
                return {...selection, colWidth};
              }
              return selection;
            });
          }
        });
      }
      columnSelectionStorageRef.current.setItem({userId, key: projectId, value: newColumnSelectionsList || []});
    },
    [userId, projectId, getColumnSelectionsByKey],
  );

  const getDefaultSelections = React.useCallback((): ColumnSelections[`selections`] => {
    return tableColumns.reduce((acc: ColumnSelections[`selections`], column) => {
      if (column.id && column.columnSelectionParams) {
        // only store if columnSelectionParams is supplied
        const isDefault = column?.columnSelectionParams.isDefault;
        if (isDefault) {
          // set shown to true
          acc.push({id: column.id, colWidth: null});
        }
      }
      return acc;
    }, []);
  }, [tableColumns]);

  const produceSelectedColumns = React.useCallback(
    ({
      shouldUseDefaults,
      savedColumnSelections,
    }: {
      shouldUseDefaults: boolean;
      savedColumnSelections?: ColumnSelections;
    }) => {
      return produce(tableColumns, (draft) => {
        draft.forEach((column, index) => {
          if (column.columnSelectionParams) {
            if (shouldUseDefaults) {
              const isDefault = column?.columnSelectionParams.isDefault;
              column.columnSelectionParams.isSelected = isDefault;
              if (typeof column.columnSelectionParams.order !== `number`) {
                column.columnSelectionParams.order = index;
              }
            } else if (savedColumnSelections) {
              const selectionIndex = savedColumnSelections.selections.findIndex((col) => col.id === column.id);
              if (selectionIndex !== -1 || !column.columnSelectionParams.canBeHidden) {
                // always ensure unhideable columns are shown
                column.columnSelectionParams.isSelected = true;
                if (typeof column.columnSelectionParams.order !== `number`) {
                  column.columnSelectionParams.order = selectionIndex;
                }

                const colWidth = savedColumnSelections.selections[selectionIndex]?.colWidth;
                if (colWidth) {
                  column.columnSelectionParams.size = colWidth;
                }
              } else {
                column.columnSelectionParams.isSelected = false;
              }
            }
          }
        });
      });
    },
    [tableColumns],
  );

  const cleanSelections = React.useCallback(
    (savedColumnSelections: ColumnSelections, savedColumnSelectionsList: ColumnSelections[]) => {
      // cleanup to only valid selections -- columns could've changed, or local storage in a bad state
      const cleanedColumnSelections = produce(savedColumnSelections, (draft) => {
        draft.selections = draft.selections.filter((selection) => {
          return !!tableColumns?.find((col) => col.id === selection.id)?.columnSelectionParams;
        });
      });
      const cleanedColumnSelectionsList = produce(savedColumnSelectionsList, (draft) => {
        // filters out potential dupes and replaces
        const newDraft = draft.filter((col) => col.key !== key);
        newDraft.push(cleanedColumnSelections);
        return newDraft;
      });
      return {cleanedColumnSelections, cleanedColumnSelectionsList};
    },
    [key, tableColumns],
  );

  useMountEffect(() => {
    if (!enableColumnSelectionStorage) {
      // immediately return if column selection is not enabled
      return;
    }
    // on mount, initialize local storage with default column selections if not present, otherwise modify table columns based on saved column selections
    const savedColumnSelectionsList = columnSelectionStorageRef.current.getItem({userId, key: projectId});
    if (!savedColumnSelectionsList) {
      // no saved column selections at all
      setModifiedTableColumns(produceSelectedColumns({shouldUseDefaults: true}));
      columnSelectionStorageRef.current.setItem({
        userId,
        key: projectId,
        value: [{key, selections: getDefaultSelections()}],
      });
    } else {
      const savedColumnSelections = getColumnSelectionsByKey(savedColumnSelectionsList);

      try {
        if (!savedColumnSelections || !savedColumnSelections.selections.length) {
          // no entity specific saved column selections
          setModifiedTableColumns(produceSelectedColumns({shouldUseDefaults: true}));
          const newSavedColumnSelectionsList = produce(savedColumnSelectionsList, (draft) => {
            // filters out potential dupes and replaces
            const newDraft = draft.filter((col) => col.key !== key);
            newDraft.push({key, selections: getDefaultSelections()});
            return newDraft;
          });
          columnSelectionStorageRef.current.setItem({userId, key: projectId, value: newSavedColumnSelectionsList});
        } else {
          // local storage present - clean up local storage just in case, modify table columns based on saved column selections
          const {cleanedColumnSelections, cleanedColumnSelectionsList} = cleanSelections(
            savedColumnSelections,
            savedColumnSelectionsList,
          );
          setModifiedTableColumns(
            produceSelectedColumns({
              shouldUseDefaults: false,
              savedColumnSelections: cleanedColumnSelections,
            }),
          );
          columnSelectionStorageRef.current.setItem({userId, key: projectId, value: cleanedColumnSelectionsList});
        }
      } catch (error) {
        // error parsing local storage, reset to defaults, don't break the table
        reporter.error(error);
        setModifiedTableColumns(produceSelectedColumns({shouldUseDefaults: true}));
        columnSelectionStorageRef.current.setItem({
          userId,
          key: projectId,
          value: [{key, selections: getDefaultSelections()}],
        });
      }
    }
  });

  return {
    columnSelectionStorage: columnSelectionStorageRef.current,
    modifiedTableColumns,
    saveColumnSelections,
    saveColumnSize,
  };
};
